import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import type {
    CellKeyDownEvent,
    ColDef,
    GridApi,
    ICellRendererParams,
    IHeaderParams,
    RowStyle,
    SelectionChangedEvent,
    ValueGetterParams,
} from 'ag-grid-community';
import { format } from 'date-fns';

import { SearchableSelectType } from 'components/inputs/searchableSelect';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';
import { FormulaType } from '../../../constants/formulaType';
import ICONS from '../../../constants/icons';
// constants
import routes from '../../../constants/routes';
import { SchedulingOptions } from '../../tables/filters/constants';

// helpers
import { BaseError } from '../../../helpers/BaseError';
import { getFormulaStartEndDate } from '../../../helpers/formulaHelpers';

import useQueryEmailTemplates from '../../../hooks/EmailTemplate/useQueryFetchTemplates';
import useAudiencesQuery from '../../../hooks/audiences/useAudiencesQuery';
import useDatasetsQuery from '../../../hooks/datasets/useDatasetsQuery';
// hooks
import useFormulaArchive from '../../../hooks/formulas/useFormulaArchive';
import useFormulaDelete from '../../../hooks/formulas/useFormulaDelete';
import useAllTopicQuery from 'hooks/topic-management/useAllTopicQuery';

// models
import {
    FormulaCategory,
    FormulaDetailModel,
    FormulaGroupByFilter,
} from '../../../models/formula.model';

import { TableFilterConfig, TableFilterType } from '../../ag-table/types';

import AGCustomHeader from '../../ag-table/AGCustomHeader';
import AGTable from '../../ag-table/AGTable';
// components
import FormulaActionMenu from '../FormulaActionMenu';
import FormulaBulkAction from '../FormulaBulkAction';
import FormulaChip from '../chips/formulaChip';
import FormulaNameTableCell from './FormulaNameTableCell';
// helpers
import {
    FilterOption,
    getAudienceFilterOptions,
    getDatasetFilterOptions,
    getScheduledFilterOptions,
    getTagFilterOptions,
    getTriggerFilterOptions,
} from './helper';

interface FormulasTableProps {
    messages: FormulaDetailModel[];
    emptyStateText: string;
    isLoading?: boolean;
    isError?: boolean;
    error?: BaseError | null;
    formulaType?: FormulaType;
    openDrawerData?: FormulaDetailModel | null;
    handleOpenDrawer: (row: FormulaDetailModel) => void;
    selectFormulaToClone: (id: number) => void;
    isClonePending: boolean;
    onGridApiReady?: (api: GridApi) => void;
    hasCreateFormulaAccess: boolean;
    hasDeleteAccess: boolean;
    gridApi: GridApi;
}

const FormulasTable: React.FC<React.PropsWithChildren<FormulasTableProps>> = ({
    messages,
    isLoading,
    emptyStateText,
    formulaType,
    handleOpenDrawer,
    openDrawerData,
    selectFormulaToClone,
    isClonePending,
    onGridApiReady,
    hasCreateFormulaAccess,
    hasDeleteAccess,
    gridApi,
}) => {
    const history = useHistory();
    const status = history.location.pathname.replace(
        `${routes.formulasMain}/`,
        '',
    ) as FormulaCategory;

    const deleteFormula = useFormulaDelete();
    const archiveFormula = useFormulaArchive();
    const { data: audiences } = useAudiencesQuery();
    const [selectedRows, setSelectedRows] = useState<FormulaDetailModel[]>([]);
    const { data: topics } = useAllTopicQuery();
    const { data: emailTemplates } = useQueryEmailTemplates();
    const { data: datasets } = useDatasetsQuery();
    const safeMessages = Array.isArray(messages) ? messages : [];

    // Reset grid state when formula category (status) changes
    // This replaces the previous approach of remounting the grid via key prop
    useEffect(() => {
        if (gridApi) {
            // Clear row selection when navigating between formula categories
            gridApi.deselectAll();
            setSelectedRows([]);
        }
    }, [status, gridApi]);

    const getRowStyle = useCallback((params: any): RowStyle | undefined => {
        if (params.node.group) {
            return {
                backgroundColor: aampeTheme.colors.backgroundWG02,
                fontWeight: aampeTheme.weights.light,
                fontSize: aampeTheme.size.font.default,
                lineHeight: aampeTheme.size.lineHeight.default,
            };
        }
        return {
            backgroundColor: aampeTheme.colors.white,
        };
    }, []);

    const timeColumnPlaceholder = useMemo<string>(() => {
        switch (status) {
            case FormulaCategory.Expired:
                return 'The date when message was marked expired';
            case FormulaCategory.Archived:
                return 'The date when message was marked archived';
            case FormulaCategory.Draft:
                return 'The date when message was last updated';
            default:
                return 'The date when message was approved ';
        }
    }, [status]);

    const audienceOptions: FilterOption[] = useMemo(
        () =>
            getAudienceFilterOptions(
                safeMessages,
                'audiences',
                audiences,
                'audience_id',
            ),
        [audiences, safeMessages],
    );
    const tagOptions: FilterOption[] = useMemo(
        () => getTagFilterOptions(safeMessages, 'tags'),
        [safeMessages],
    );
    const triggerOptions: FilterOption[] = useMemo(
        () => getTriggerFilterOptions(safeMessages, 'compound_trigger'),
        [safeMessages],
    );

    const scheduledOptions: FilterOption[] = useMemo(
        () =>
            getScheduledFilterOptions(
                safeMessages,
                'is_controlled_message',
                SchedulingOptions,
            ),
        [safeMessages],
    );

    const datasetOptions: FilterOption[] = useMemo(
        () => getDatasetFilterOptions(safeMessages, datasets),
        [safeMessages, datasets],
    );

    const filtersConfig = useMemo<TableFilterConfig[]>(
        () => [
            {
                field: 'is_controlled_message',
                label: 'Scheduling',
                type: TableFilterType.MULTISELECT,
                cellDataType: 'text',
                meta: {
                    options: scheduledOptions,
                },
            },
            {
                field: 'audiences',
                label: 'Audience',
                type: TableFilterType.MULTISELECT,
                cellDataType: 'text',
                meta: {
                    options: audienceOptions,
                    filterType: 'equal',
                },
            },
            {
                field: 'compound_trigger',
                label: 'Trigger',
                type: TableFilterType.MULTISELECT,
                cellDataType: 'text',
                meta: {
                    options: triggerOptions,
                    filterType: 'equal',
                },
            },
            {
                field: 'tags',
                label: 'Tags',
                type: TableFilterType.MULTISELECT,
                cellDataType: 'text',
                meta: {
                    options: tagOptions,
                },
            },
            {
                field: 'dataset_id',
                label: 'Dataset',
                type: TableFilterType.MULTISELECT,
                cellDataType: 'text',
                meta: {
                    options: datasetOptions,
                    filterType: 'equal',
                },
            },
            {
                field: 'group_by',
                showFilterChip: false,
                label: 'Group By',
                type: TableFilterType.DROPDOWN,
                setQueryParamsOnly: true,
                defaultValue: {
                    label: 'None',
                    value: FormulaGroupByFilter.None,
                } as SearchableSelectType,
                cellDataType: 'text',
                meta: {
                    options: [
                        { label: 'Topic', value: FormulaGroupByFilter.Topic },
                        {
                            label: 'Template',
                            value: FormulaGroupByFilter.Template,
                        },
                        { label: 'None', value: FormulaGroupByFilter.None },
                    ],
                },
            },
        ],
        [
            audienceOptions,
            datasetOptions,
            scheduledOptions,
            tagOptions,
            triggerOptions,
        ],
    );

    const allEmailTemplates = useMemo(
        () =>
            emailTemplates?.map((t) => ({
                email_template_id: t.email_template_id,
                template_name: t.template_name,
                is_aampe_custom_template: t.is_aampe_custom_template,
            })) || [],
        [emailTemplates],
    );
    const handleSelectionChange = (event: SelectionChangedEvent) => {
        const selectedNodes = event.api
            .getSelectedRows()
            .filter((node) => node.has_write_access);
        setSelectedRows(selectedNodes);
    };

    const selectedIds = useMemo(() => {
        return selectedRows.map((node) => node.id);
    }, [selectedRows]);

    const groupBy = useMemo<FormulaGroupByFilter>(() => {
        const params = new URLSearchParams(location.search);
        return (
            (params.get('group_by') as FormulaGroupByFilter) ??
            FormulaGroupByFilter.None
        );
    }, [location.search]);

    const checkboxColumn = useMemo<ColDef<FormulaDetailModel>[]>(() => {
        if (!hasCreateFormulaAccess) return [];

        return [
            {
                headerName: '',
                checkboxSelection: (params) =>
                    params.data?.has_write_access === true,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                width: 44,
                minWidth: 44,
                maxWidth: 44,
                cellStyle: {
                    padding: '0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                cellClass: 'centered-checkbox',
                pinned: 'left',
                sortable: false,
                filter: false,
                resizable: false,
                suppressMenu: true,
                suppressSizeToFit: true,
            },
        ];
    }, [hasCreateFormulaAccess]);

    const isGroupByTopic = useMemo<boolean>(() => {
        return groupBy === FormulaGroupByFilter.Topic;
    }, [groupBy]);

    const isGroupByTemplate = useMemo<boolean>(() => {
        return groupBy === FormulaGroupByFilter.Template;
    }, [groupBy]);

    const columnDefs = React.useMemo<ColDef[]>(() => {
        const baseColumns: ColDef[] = [
            ...checkboxColumn,
            {
                headerName: 'Message name',
                field: 'name',
                resizable: true,
                valueGetter: (
                    params: ValueGetterParams<FormulaDetailModel, string>,
                ) => params.data?.name || 'Untitled Message',
                cellRenderer: (params: any) => {
                    if (params.node.group || !params.data) return null;
                    return (
                        <StyledFormulaContainer>
                            <FormulaNameTableCell
                                data={params.data}
                                node={params.node}
                                isDrawerOpen={
                                    openDrawerData?.id === params.data.id
                                }
                                onDrawerOpen={() =>
                                    handleOpenDrawer(params.data)
                                }
                            />
                        </StyledFormulaContainer>
                    );
                },
                tooltipValueGetter: () => null,
                flex: 1,
                minWidth: 500,
                headerComponent: (props: IHeaderParams) => (
                    <StyledHeaderContainer>
                        <AGCustomHeader
                            props={props}
                            toolTipTitle="This column shows the Message Group Name (Note: This is not visible to customers)."
                        />
                    </StyledHeaderContainer>
                ),
            },
            {
                headerName: 'Template',
                colId: 'email_template_id',
                hide: isGroupByTemplate || formulaType !== FormulaType.email,
                resizable: isGroupByTemplate ? false : true,
                rowGroup: isGroupByTemplate,
                suppressHeaderMenuButton: true,
                minWidth: 150,
                valueGetter: (
                    params: ValueGetterParams<FormulaDetailModel, string>,
                ) => {
                    if (!params.data) return null;
                    const templateId =
                        params.data?.additional_metadata?.email_template_id;
                    if (!templateId) return 'No Template';
                    const template = allEmailTemplates?.find(
                        (t) => t.email_template_id === templateId,
                    );
                    return template?.template_name || templateId;
                },
                tooltipValueGetter: (params) => {
                    const templateId =
                        params.data?.additional_metadata?.email_template_id;
                    if (!templateId) return null;
                    const template = allEmailTemplates?.find(
                        (t) => t.email_template_id === templateId,
                    );
                    if (template) return template.template_name;
                    return `${templateId} — This template could not be found. It may be an external template (e.g. SendGrid, Braze) that is not currently accessible.`;
                },
                cellRenderer: (params: any) => {
                    if (!isGroupByTemplate) {
                        const templateId =
                            params.data?.additional_metadata?.email_template_id;
                        if (!templateId) return 'No Template';
                        const template = allEmailTemplates?.find(
                            (t) => t.email_template_id === templateId,
                        );
                        const name = template?.template_name || templateId;
                        return (
                            <TemplateCellContainer>
                                {name}
                                {template?.is_aampe_custom_template && (
                                    <AampeLogoIcon />
                                )}
                            </TemplateCellContainer>
                        );
                    }
                    return '';
                },
            },
            {
                headerName: 'Alternates',
                field: 'message_count',
                valueGetter: (
                    params: ValueGetterParams<FormulaDetailModel, number>,
                ) => params.data?.message_count ?? 0,
                width: 200,
                sortable: false,
                headerComponent: (props: IHeaderParams) => (
                    <StyledHeaderContainer>
                        <AGCustomHeader
                            props={props}
                            toolTipTitle="The number of complete, unique, sendable messages generated by all combinations of individual text variants included in this message."
                        />
                    </StyledHeaderContainer>
                ),
            },
            {
                headerName: 'Alternate labels',
                field: 'alternate_labels_count',
                valueGetter: (
                    params: ValueGetterParams<FormulaDetailModel, number>,
                ) => params.data?.alternate_labels_count ?? 0,
                width: 200,
                minWidth: 150,
                sortable: false,
                headerComponent: (props: IHeaderParams) => (
                    <StyledHeaderContainer>
                        <AGCustomHeader
                            props={props}
                            toolTipTitle="Number of labels attached to alternates."
                        />
                    </StyledHeaderContainer>
                ),
            },
            {
                headerName: 'Dataset',
                colId: 'dataset_display',
                suppressHeaderMenuButton: true,
                minWidth: 120,
                valueGetter: (
                    params: ValueGetterParams<FormulaDetailModel, string>,
                ) => {
                    if (!params.data?.dataset_id) return 'No Dataset';
                    return (
                        datasets?.find((d) => d.id === params.data?.dataset_id)
                            ?.name ?? 'No Dataset'
                    );
                },
                cellRenderer: (params: any) => {
                    if (params.node.group || !params.data) return null;
                    const datasetId = params.data.dataset_id;
                    const name = datasetId
                        ? datasets?.find((d) => d.id === datasetId)?.name ??
                          'No Dataset'
                        : 'No Dataset';
                    if (!datasetId)
                        return <NoDatasetText>{name}</NoDatasetText>;
                    return (
                        <DatasetLink
                            onClick={(e) => {
                                e.stopPropagation();
                                history.push(routes.datasetsView(datasetId));
                            }}
                        >
                            {name}
                        </DatasetLink>
                    );
                },
            },
            {
                headerName: 'Dataset',
                field: 'dataset_id',
                valueGetter: (params) =>
                    params.data?.dataset_id != null
                        ? String(params.data.dataset_id)
                        : null,
                sortable: false,
                hide: true,
                filter: 'agTextColumnFilter',
                filterParams: {
                    maxNumConditions: datasetOptions?.length ?? 4,
                },
            },
            {
                headerName: 'Topic',
                field: 'topic_id',
                hide: isGroupByTopic,
                resizable: isGroupByTopic ? false : true,
                rowGroup: isGroupByTopic,
                suppressHeaderMenuButton: true,
                minWidth: 100,
                valueGetter: (
                    params: ValueGetterParams<FormulaDetailModel, string>,
                ) => {
                    if (!params.data) return null;
                    const topicName = topics?.find(
                        (topic) => topic.id == params.data?.topic_id,
                    )?.name;
                    return topicName ?? 'No Topic';
                },
                cellRenderer: (params: any) => {
                    if (!isGroupByTopic) {
                        const topicName = topics?.find(
                            (topic) => topic.id == params?.data?.topic_id,
                        )?.name;
                        return topicName ?? 'No Topic';
                    }
                    return '';
                },
            },
            {
                headerName: 'Trigger',
                field: 'compound_trigger',
                valueGetter: (params) => params.data?.compound_trigger?.id,
                sortable: false,
                hide: true,
                filter: 'agTextColumnFilter',
                filterParams: { maxNumConditions: triggerOptions?.length ?? 4 },
            },
            {
                headerName: 'Audiences',
                field: 'audiences',
                valueGetter: (params) =>
                    params.data?.audiences?.[0]?.audience_id,
                sortable: false,
                hide: true,
                filter: 'agTextColumnFilter',
                filterParams: {
                    maxNumConditions: audienceOptions?.length ?? 4,
                },
            },
            {
                headerName: 'Delivery',
                field: 'is_controlled_message',
                valueGetter: (params) => params.data?.is_controlled_message,
                sortable: false,
                hide: true,
                filter: 'agTextColumnFilter',
            },
            {
                headerName: 'Tags',
                field: 'tags',
                valueGetter: (params) =>
                    params.data?.tags?.map((tag: any) => tag.name).join(', '),
                cellRenderer: (params: any) => {
                    if (params.node.group || !params.data) return null;
                    return <FormulaChip items={params.data.tags!} />;
                },
                width: 160,
                filter: 'agTextColumnFilter',
                filterParams: { maxNumConditions: tagOptions?.length ?? 4 },
                headerComponent: (props: IHeaderParams) => (
                    <StyledHeaderContainer>
                        <AGCustomHeader
                            props={props}
                            toolTipTitle="Tags are provided as a means to help you organize your catalog. Tags are only used for your convenience. They will not be used in learning or visible to your customers."
                        />
                    </StyledHeaderContainer>
                ),
            },
        ];

        if (![FormulaCategory.Review, FormulaCategory.Draft].includes(status)) {
            let heading = 'Activated';
            if (status === FormulaCategory.Live) {
                heading = 'End Date';
            } else if (status === FormulaCategory.Scheduled) {
                heading = 'Activates';
            }

            baseColumns.push({
                headerName: heading,
                field: 'send_start_or_send_end',
                headerComponent: (props: IHeaderParams) => (
                    <StyledHeaderContainer>
                        <AGCustomHeader
                            props={props}
                            toolTipTitle={
                                status === FormulaCategory.Live
                                    ? 'The end date of message'
                                    : 'The date when message was marked approved.'
                            }
                        />
                    </StyledHeaderContainer>
                ),
                valueGetter: (
                    params: ValueGetterParams<FormulaDetailModel, any>,
                ) => {
                    const { send_start, send_stop } = getFormulaStartEndDate(
                        params.data || null,
                    );
                    if (FormulaCategory.Live === status && send_stop) {
                        return new Date(send_stop);
                    } else if (send_start) {
                        return new Date(send_start);
                    }
                    return params.data?.approved_at
                        ? new Date(params.data?.approved_at)
                        : '-';
                },
                cellRenderer: (params: any) => {
                    if (!params.data) return '-';
                    const { send_start, send_stop } = getFormulaStartEndDate(
                        params.data,
                    );
                    if (FormulaCategory.Live === status) {
                        return send_stop
                            ? format(new Date(send_stop), 'dd-MM-yyyy')
                            : '-';
                    }
                    return send_start
                        ? format(new Date(send_start), 'dd-MM-yyyy')
                        : params.data?.approved_at
                        ? format(
                              new Date(params.data?.approved_at),
                              'dd-MM-yyyy',
                          )
                        : '-';
                },
                width: 132,
                sortable: true,
                tooltipValueGetter: () => null,
            });
        }

        baseColumns.push({
            headerName:
                status === FormulaCategory.Archived
                    ? 'Archived'
                    : status === FormulaCategory.Expired
                    ? 'Expired'
                    : 'Updated',
            field: 'updated_at',
            headerComponent: (props: IHeaderParams) => (
                <StyledHeaderContainer>
                    <AGCustomHeader
                        props={props}
                        toolTipTitle={timeColumnPlaceholder}
                    />
                </StyledHeaderContainer>
            ),
            valueGetter: (
                params: ValueGetterParams<FormulaDetailModel, Date>,
            ) =>
                params.data?.updated_at
                    ? new Date(params.data.updated_at)
                    : new Date(0),
            cellRenderer: (
                params: ICellRendererParams<FormulaDetailModel, any>,
            ) => {
                if (!params.data) return '-';

                let displayDate;

                switch (status) {
                    case FormulaCategory.Archived:
                        displayDate = params.data.archived_at;
                        break;

                    case FormulaCategory.Expired:
                        displayDate = params.data.send_stop_date;
                        break;

                    default:
                        displayDate = params.data.updated_at;
                        break;
                }

                return format(
                    new Date(displayDate ?? new Date()),
                    'dd-MM-yyyy',
                );
            },
            width: 132,
            sortable: true,
        });

        if (hasCreateFormulaAccess) {
            baseColumns.push({
                headerName: '',
                field: 'status',
                width: 120,
                sortable: true,
                tooltipValueGetter: () => null,
                pinned: 'right',
                cellRenderer: (params: any) => {
                    if (!params.data) return null;
                    return (
                        <FormulaActionMenu
                            key={params.data?.id}
                            rowData={params.data}
                            deleteFormula={deleteFormula}
                            archiveFormula={archiveFormula}
                            selectFormulaToClone={selectFormulaToClone}
                            isClonePending={isClonePending}
                            containerStyle={{
                                justifyContent: 'center',
                            }}
                            hasDeleteAccess={hasDeleteAccess}
                        />
                    );
                },
            });
        }

        return baseColumns;
    }, [
        triggerOptions.length,
        audienceOptions.length,
        datasetOptions.length,
        tagOptions.length,
        status,
        deleteFormula,
        archiveFormula,
        timeColumnPlaceholder,
        hasCreateFormulaAccess,
        openDrawerData?.id,
        handleOpenDrawer,
        isClonePending,
        selectFormulaToClone,
        hasDeleteAccess,
        topics,
        allEmailTemplates,
        datasets,
        groupBy,
        isGroupByTopic,
        isGroupByTemplate,
    ]);

    const rowStyle = useMemo(() => ({ maxHeight: '60vh' }), []);

    const handleCellKeyDown = useCallback(
        (event: CellKeyDownEvent<FormulaDetailModel>) => {
            const keyboardEvent = event.event as KeyboardEvent;
            const key = keyboardEvent.key;

            if (openDrawerData === null) return;

            const rowIndex = event.node.rowIndex;
            if (rowIndex === null) return;
            const api = event.api;
            const totalRows = api.getDisplayedRowCount();

            let targetIndex: number | null = null;

            if (key === 'ArrowDown' && rowIndex < totalRows - 1) {
                targetIndex = rowIndex + 1;
            } else if (key === 'ArrowUp' && rowIndex > 0) {
                targetIndex = rowIndex - 1;
            }

            if (targetIndex !== null) {
                const targetNode = api.getDisplayedRowAtIndex(targetIndex);
                if (targetNode && targetNode.data) {
                    api.ensureIndexVisible(targetIndex);
                    api.setFocusedCell(targetIndex, event.column);
                    api.deselectAll();
                    targetNode.setSelected(true);
                    handleOpenDrawer(targetNode.data);
                }
            }
        },
        [openDrawerData, handleOpenDrawer],
    );

    const handleClose = () => {
        setSelectedRows([]);
        gridApi.deselectAll();
    };
    return (
        <StyledAGTable
            columns={columnDefs}
            rowData={safeMessages}
            filtersConfig={selectedRows.length ? [] : filtersConfig}
            emptyStateText={emptyStateText}
            rowStyle={rowStyle}
            getRowStyle={getRowStyle}
            allowSearching
            isDataLoading={isLoading}
            handleSelectionChange={handleSelectionChange}
            pagination={false}
            customActions={
                selectedRows.length ? (
                    <FormulaBulkAction
                        selectedIds={selectedIds}
                        selectedRows={selectedRows}
                        handleClose={handleClose}
                        isDisabled={false}
                    />
                ) : null
            }
            onFirstDataRendered={(params) => {
                params.api.refreshHeader();
                params.api.resetRowHeights();
            }}
            suppressCellFocus={false}
            onCellKeyDown={handleCellKeyDown}
            onGridApiReady={onGridApiReady}
            className="drawer-click-exempt formulas-table"
            customGridOptions={{
                groupDisplayType: 'groupRows',
                groupDefaultExpanded: -1,
                suppressContextMenu: true,
            }}
        />
    );
};

export default memo(FormulasTable);

const StyledFormulaContainer = styled.div({
    display: 'flex',
});

const StyledHeaderContainer = styled.div({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
});

const TemplateCellContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
});

const AampeLogoIcon = styled(ICONS.aampeLogo)({
    height: 16,
    width: 19,
});

const DatasetLink = styled.span({
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
});

const NoDatasetText = styled.span({
    color: aampeTheme.colors.solidWG01,
});

const StyledAGTable = styled(AGTable)({
    padding: 0,
    margin: 0,
    '.ag-row-group:hover, .ag-row-group.ag-row-hover': {
        backgroundColor: aampeTheme.colors.backgroundWG02,
    },
});
